import { gql } from '@apollo/client';
import { BankIcon, BillIcon, BuildingIcon, DollarIcon, RecurringIcon } from '@orn/shared-web/icons';
import { entityChipLabel } from '@orn/shared-web/lib/assistant/entityChipLabel';
import { cx } from '@orn/shared-web/utils/cx';
import isPlainLeftClick from '@orn/shared-web/utils/isPlainLeftClick';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { entityHref } from '../../utils/entityDestination';
import Entity from '../entity';
import { entityLogoSrc } from '../entity/EntityAvatar';

import type {
  EntityChipAccountFragment,
  EntityChipBillFragment,
  EntityChipMerchantFragment,
  EntityChipRecurringTransactionFragment,
  EntityChipTransactionFragment,
} from './__generated__/EntityChip.gql';
import type { EntityRefType } from '../../lib/assistant/remarkAssistantMarkup';
import type { EntityTarget } from '../../utils/entityDestination';

/**
 * The rows a chip can name. One per record type the assistant can reference —
 * the same five the token grammar admits, so a resolvable token always has a
 * chip to render into.
 */
export type EntityChipTarget =
  | ({ __typename: 'Merchant' } & EntityChipMerchantFragment)
  | ({ __typename: 'FinancialAccount' } & EntityChipAccountFragment)
  | ({ __typename: 'Transaction' } & EntityChipTransactionFragment)
  | ({ __typename: 'Bill' } & EntityChipBillFragment)
  | ({ __typename: 'RecurringTransaction' } & EntityChipRecurringTransactionFragment);

const MerchantFragment = gql`
  fragment EntityChipMerchant on Merchant {
    id
    name
    ...EntityAvatar
  }
  ${Entity.Avatar.Fragment}
`;

const AccountFragment = gql`
  fragment EntityChipAccount on FinancialAccount {
    id
    name
    mask
    ...EntityAvatar
  }
  ${Entity.Avatar.Fragment}
`;

const TransactionFragment = gql`
  fragment EntityChipTransaction on Transaction {
    id
    amount
  }
`;

// Both select the merchant because the label coalesces merchant-first, the way
// the slideout a click opens titles itself — see `entityChipLabel` — and because
// a bill and a stream draw that merchant's logo as their own leading visual.
const BillFragment = gql`
  fragment EntityChipBill on Bill {
    id
    providerName
    merchant {
      id
      name
      ...EntityAvatar
    }
  }
  ${Entity.Avatar.Fragment}
`;

const RecurringTransactionFragment = gql`
  fragment EntityChipRecurringTransaction on RecurringTransaction {
    id
    description
    merchant {
      id
      name
      ...EntityAvatar
    }
  }
  ${Entity.Avatar.Fragment}
`;

/**
 * The token's record type as the client names the same row. One adapter at the
 * one boundary that needs it, so the assistant's five-member grammar and the
 * client's openable-row union stay free to differ — see
 * [`utils/entityDestination`](../../utils/entityDestination.ts).
 */
const TARGET_TYPE: Record<EntityRefType, EntityTarget['type']> = {
  merchant: 'merchant',
  financial_account: 'financialAccount',
  transaction: 'transaction',
  bill: 'bill',
  recurring_transaction: 'recurringTransaction',
};

/**
 * The glyph a chip shows in place of a logo, colored per type the way Figma's
 * "Clickable References" set colors it. A transaction always lands here — its
 * currency glyph is the `$` its label omits, not a fallback for a missing logo.
 */
const CHIP_GLYPH: Record<EntityRefType, { Icon: React.FC<React.SVGProps<SVGSVGElement>>; className: string }> = {
  merchant: { Icon: BuildingIcon, className: 'text-utility-stone-500' },
  financial_account: { Icon: BankIcon, className: 'text-utility-stone-500' },
  transaction: { Icon: DollarIcon, className: 'text-success-primary' },
  bill: { Icon: BillIcon, className: 'text-utility-brand-500' },
  recurring_transaction: { Icon: RecurringIcon, className: 'text-utility-blue-dark-500' },
};

// `leading-[18px]` overrides the theme's own `text-sm` line height of 20px.
// Figma's `Line height/text-sm` token is 18, and inside a pill the difference
// is 2px of height rather than 2px of leading — the two disagree app-wide, and
// the chip is where it shows.
const CHIP_CLASS =
  'bg-tertiary border-primary text-secondary rounded-xs mx-px inline-flex items-center gap-1 border-[0.8px] px-1 py-0.5 align-middle text-sm font-semibold leading-[18px]';

/** Figma's Hover state is the Default state on `bg-primary`; nothing else moves. */
const INTERACTIVE_CLASS =
  'hover:bg-primary outline-focus-ring cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-offset-1';

/** The logo a chip draws — a bill and a stream borrow their merchant's. */
const chipLogoSrc = (target: EntityChipTarget): string | null => {
  switch (target.__typename) {
    case 'Merchant':
    case 'FinancialAccount':
      return entityLogoSrc(target);
    case 'Bill':
    case 'RecurringTransaction':
      return target.merchant ? entityLogoSrc({ ...target.merchant, __typename: 'Merchant' }) : null;
    default:
      return null;
  }
};

interface IChipVisualProps {
  /** Which record type the chip names — picks the glyph and its color. */
  refType: EntityRefType;
  /** The logo to draw, or `null` where the row has none. */
  src: string | null;
}

/**
 * A chip's leading visual: the row's own logo, or its type's glyph where there
 * is none. The fallback lives here rather than at the call site so a logo that
 * 404s lands on the same glyph an absent one does.
 *
 * The glyph is 14px inside a 16px box, matching Figma — a logo fills that box
 * edge to edge, so both variants take the same width and swapping one for the
 * other never reflows the sentence around it.
 */
const ChipVisual = ({ refType, src }: IChipVisualProps) => {
  // Remembers which URL failed rather than that one did: a chip outlives the row
  // it draws — the transcript-wide query can push a corrected logo into a mounted
  // chip — and a bare flag would hold the glyph against the new image too.
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null);
  const { Icon, className } = CHIP_GLYPH[refType];

  if (src !== null && src !== failedSrc) {
    return (
      <img
        src={src}
        // Decorative: the label beside it is the chip's accessible name.
        alt=""
        onError={() => setFailedSrc(src)}
        className="border-secondary_alt rounded-xs size-4 shrink-0 border-[0.5px] object-cover"
      />
    );
  }

  return (
    <span className="flex size-4 shrink-0 items-center justify-center">
      <Icon className={cx('size-3.5 shrink-0', className)} aria-hidden="true" />
    </span>
  );
};

interface IEntityChipProps {
  /** Which record type the token named — drives the fallback and the leading visual. */
  refType: EntityRefType;
  /** The resolved row, or `null` when the id did not resolve. */
  target: EntityChipTarget | null;
  /**
   * Open the row this chip names. Omitted — in Storybook, or before the pane
   * wires it — the pill renders inert rather than disabled, for the same reason
   * {@link SuggestionChip} does: a greyed-out chip reads as "unavailable" when
   * the truth is that nothing is listening.
   */
  onSelect?: (target: EntityTarget) => void;
}

/**
 * An inline reference to a row the user can see, rendered mid-sentence inside
 * an assistant reply: a bordered pill carrying the entity's own logo (or its
 * type's glyph) and its name.
 *
 * The label comes from {@link entityChipLabel}, never from the model — the
 * assistant emits only `<merchant_id=… />` and the client resolves it, so a
 * chip cannot name one merchant while the underlying row is another.
 *
 * Renders as one of three elements, and which one is a property of the row
 * rather than a prop: a link where the row has a URL, a button where opening it
 * means a slideout, and a plain span where there is nothing to open — an
 * unresolved id names no row, and no handler means nothing is listening.
 */
const EntityChip = ({ refType, target, onSelect }: IEntityChipProps) => {
  const body = (
    <>
      {/* An unresolved chip carries no visual: it names no row, so it has no
          logo, and the transaction glyph is a `$` with no amount to sit before. */}
      {target === null ? null : <ChipVisual refType={refType} src={chipLogoSrc(target)} />}
      {entityChipLabel(refType, target)}
    </>
  );

  // Figma tightens only the Amount variant, where the gap separates a currency
  // glyph from the figure it belongs to rather than a logo from a name.
  const chipClass = cx(CHIP_CLASS, refType === 'transaction' && 'gap-0.5');

  const entityTarget: EntityTarget | null =
    target === null || onSelect === undefined ? null : { type: TARGET_TYPE[refType], id: target.id };

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) {
      return;
    }
    event.preventDefault();
    if (entityTarget !== null) {
      onSelect?.(entityTarget);
    }
  };

  if (entityTarget === null) {
    return <span className={chipClass}>{body}</span>;
  }

  const href = entityHref(entityTarget);
  if (href === null) {
    return (
      <button type="button" className={cx(chipClass, INTERACTIVE_CLASS)} onClick={() => onSelect?.(entityTarget)}>
        {body}
      </button>
    );
  }

  // A real `to`, not a click handler on a span: the row has an address, so
  // cmd-click and copy-link-address have to behave the way they do everywhere
  // else. The handler only claims the plain left-click, which navigates the
  // content column while leaving the conversation open.
  return (
    <RouterLink to={href} className={cx(chipClass, INTERACTIVE_CLASS)} onClick={handleClick}>
      {body}
    </RouterLink>
  );
};

export default Object.assign(EntityChip, {
  MerchantFragment,
  AccountFragment,
  TransactionFragment,
  BillFragment,
  RecurringTransactionFragment,
});
